import produce from "immer";
import { omit, unset } from "lodash";
import moment from "moment";
import { noop, Observable } from "rxjs";
import { Option } from "space-monad";
import { store } from "..";
import { withProgress } from "../../components/loader/file-progress";
import { ResultWithProgress } from "../../components/loader/models";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { deepRemoveEmptyObjects, mapObject } from "../../utils/utils";
import { addFieldsToForm, compactObj, flattenObj } from "../common/forms";
import { generateDownload } from "../common/utils";
import { processErrorFromPromise } from "../errors/utils";
import { triggerErrorEvent, triggerEvent } from "../eventstore/trigger-event";
import { DefaultFieldNames } from "../launch-configurations/models/default-fields";
import { LaunchConfigurationsDTO } from "../launch-configurations/models/dto";
import { Field } from "../launch-configurations/models/internal/field";
import { LaunchesDTO } from "../launches/models/dto";
import { successNotificationFromPromise } from "../notifications/utils";
import { ParticipationsDTO } from "./models/dto";

export namespace ParticipationsService {
  const api = new API("kyc-upload");

  export function getParticipations(
    pagination: Pageable.T<keyof ParticipationsDTO.Responses.GetSingle.T> = {}
  ): Promise<ParticipationsDTO.Responses.GetAll.T> {
    return api
      .post(`v2/participation/search`)
      .query(Pageable.getQueryParam(omit(pagination, "filters")))
      .send(
        Option(pagination.filters)
          .map((f) =>
            f.reduce((acc, curr) => ({ ...acc, [curr.field]: curr.value }), {})
          )
          .getOrElse({})
      )
      .then<ParticipationsDTO.Responses.GetAll.T>((response) => response.body)
      .catch<ParticipationsDTO.Responses.GetAll.T>((x) => Promise.reject(x));
  }

  export function getConfigurations(
    pagination: Pageable.T<
      keyof LaunchConfigurationsDTO.Responses.GetSingle.T
    > = {}
  ): Promise<LaunchConfigurationsDTO.Responses.GetAll.T> {
    return api
      .get(`ui/configurations`)
      .query(Pageable.getQueryParam(pagination))
      .then<LaunchConfigurationsDTO.Responses.GetAll.T>(
        (response) => response.body
      )
      .catch<LaunchConfigurationsDTO.Responses.GetAll.T>(triggerErrorEvent);
  }

  export function getParticipation(
    id: ParticipationsDTO.Responses.GetSingle.T["id"]
  ): Promise<ParticipationsDTO.Responses.GetSingle.T> {
    return api
      .get(`v2/participation/${id}`)
      .then<ParticipationsDTO.Responses.GetSingle.T>(
        (response) => response.body
      )
      .catch<ParticipationsDTO.Responses.GetSingle.T>(processErrorFromPromise);
  }

  export function removeParticipation(
    id: ParticipationsDTO.Responses.GetSingle.T["id"]
  ): Promise<void> {
    return api
      .delete(`v2/participation/${id}`)
      .then(noop)
      .then(triggerEvent("REMOVE_PARTICIPATION_SUCCESS"))
      .catch<void>(triggerErrorEvent);
  }

  export function getParticipationSummary(): Promise<ParticipationsDTO.Responses.GetParticipationSummary.T> {
    var dateOffset = 5 * 7 * 24 * 60 * 60 * 1000; //4 weeks
    var startDate = new Date();
    startDate.setTime(startDate.getTime() - dateOffset);

    return api
      .get(`v2/participation/requests`)
      .query({ from: moment(startDate).format("yyyy-MM-DD") })
      .then<ParticipationsDTO.Responses.GetParticipationSummary.T>(
        (response) => response.body
      )
      .catch<ParticipationsDTO.Responses.GetParticipationSummary.T>(
        triggerErrorEvent
      );
  }

  export function updateParticipation(
    id: ParticipationsDTO.Responses.GetSingle.T["id"],
    data: ParticipationsDTO.Requests.SetSingle.T
  ): Promise<ParticipationsDTO.Responses.GetSingle.T> {
    return api
      .put(`v2/participation/${id}`)
      .send(data)
      .then<ParticipationsDTO.Responses.GetSingle.T>(
        (response) => response.body
      )
      .then(triggerEvent("UPDATE_PARTICIPATION_SUCCESS"))
      .catch<ParticipationsDTO.Responses.GetSingle.T>(triggerErrorEvent);
  }

  export function postParticipation(
    configurationId: ParticipationsDTO.Responses.GetSingle.T["id"],
    data: LaunchesDTO.Requests.ParticipationForm.ForForm,
    authedFields: Array<DefaultFieldNames>,
    allFields?: Array<Field.Field<string>>
  ): Observable<ResultWithProgress<LaunchesDTO.Responses.ParticipationForm.T>> {
    const login = store.getState().auth.login;

    // Get the correct endpoint depending if the user is logged in
    const url =
      login.status === "ready" && login.data.accessToken.authenticated
        ? "v2/participation/authenticated"
        : "v2/participation/anonymous";

    const normalizeDataForApi = (allFields?: Array<Field.Field<string>>) => (
      datum: LaunchesDTO.Requests.ParticipationForm.ForForm
    ) =>
      produce<
        LaunchesDTO.Requests.ParticipationForm.ForForm,
        LaunchesDTO.Requests.ParticipationForm.ForForm,
        LaunchesDTO.Requests.ParticipationForm.T
      >(datum, (data) => ({
        ...data,
        ...(data.custom
          ? {
            customQuestions: mapObject(data.custom, (val, fieldName) => ({
              fieldName,
              answer: Array.isArray(val) ? val.join(",") : val,
              type: allFields?.find((f) => f.field === `custom.${fieldName}`)
                ?.type,
            })),
          }
          : {}),

        regulations: {
          ...data.regulations,
          resideSanctionedCountry: !data.regulations.resideSanctionedCountry,
          usPerson: !data.regulations.usPerson,
        },
      }));

    // Remove unnecessary data if the user is logged in
    const newData = LaunchesDTO.Requests.ParticipationFormActual.mapToActualDTO(
      login.status === "ready" && login.data.accessToken.authenticated
        ? deepRemoveEmptyObjects(
          produce(data, (d) => {
            authedFields.forEach((curr) => unset(d, curr));
          })
        )
        : normalizeDataForApi(allFields)(data)
    );
    return withProgress(
      addFieldsToForm(
        api.post(url),
        compactObj(flattenObj({ ...newData, configurationId }, true))
      ),
      (r) =>
        r
          .responseType("json")
          .then((response) => response.body)
          .then(triggerEvent("POST_PARTICIPATION_SUCCESS"))
          .catch(triggerErrorEvent)
    );
  }

  export function requestOtp(data: {
    configurationId: string;
    emailAddress: string;
    captcha: string;
    locale: string;
  }) {
    return api
      .post(`v1/otp/request`)
      .send({ ...data, locale: data.locale })
      .then<void>(() => { })
      .then(successNotificationFromPromise("email-verification-sent"))
      .catch<void>(processErrorFromPromise);
  }

  export function verifyOtp(data: {
    configurationId: string;
    emailAddress: string;
    otpCode: string;
    captcha: string;
    locale: string;
  }): Promise<LaunchesDTO.Responses.VerifyOtp.T> {
    return api
      .post(`v1/otp/verify`)
      .send(data)
      .then<LaunchesDTO.Responses.VerifyOtp.T>((res) => {
        if (!res.body?.success)
          return Promise.reject(new Error("otp-is-incorrect"));
        return res.body;
      })
      .then(successNotificationFromPromise("email-successfully-verified"))
      .catch<LaunchesDTO.Responses.VerifyOtp.T>(processErrorFromPromise);
  }

  export const downloadCsv = (
    configIds?: Array<{ id: string; launchName: string }>
  ) =>
    generateDownload(
      api,
      `v2/participation${configIds && configIds.length
        ? "?configurationIds=" + configIds.map((x) => x.id).join(",")
        : ""
      }`,
      {
        headers: {
          Accept: "text/csv",
        },
        verb: "get",
      }
    )();

  export function checkPreviousParticipation(data: {
    emailAddress: string;
    configurationId: string;
  }): Promise<LaunchesDTO.Responses.CheckPreviousParticipation.T> {
    return api
      .post(`v2/participation/applications/prev`)
      .send(data)
      .then<LaunchesDTO.Responses.CheckPreviousParticipation.T>((res) => {
        if (res.body?.previousApplicationFound) {
          return Promise.reject(
            new Error("you-have-already-applied-for-this-launch")
          );
        }
        return res.body;
      })
      .catch<LaunchesDTO.Responses.CheckPreviousParticipation.T>(
        processErrorFromPromise
      );
  }
}
